import type { ReactNode } from 'react';
import {
  ClipboardList, CheckSquare, FilePen, BookOpen, MoreHorizontal,
  Home, BarChart3, CreditCard, Megaphone, Settings, Users,
  DollarSign, FileText, Receipt, ArrowLeftToLine, Wallet,
  Calendar, FileSpreadsheet, GraduationCap,
  Landmark, Scale, UserPlus, Contact, BookMarked,
  CalendarDays, CalendarRange, Award, PieChart,
} from 'lucide-react';
import type { Role } from '../stores/authStore';

export type NavItem = { to: string; label: string; icon: ReactNode; children?: NavItem[] };

const NAV: Partial<Record<Role, readonly NavItem[]>> = {
  Teacher: [
    { to: '/today', label: 'Today', icon: <ClipboardList size={20} aria-hidden="true" /> },
    { to: '/attendance', label: 'Attendance', icon: <CheckSquare size={20} aria-hidden="true" /> },
    { to: '/marks', label: 'Marks', icon: <FilePen size={20} aria-hidden="true" /> },
    { to: '/homework', label: 'Homework', icon: <BookOpen size={20} aria-hidden="true" /> },
    { to: '/more', label: 'More', icon: <MoreHorizontal size={20} aria-hidden="true" /> },
  ],
  Parent: [
    { to: '/home', label: 'Home', icon: <Home size={20} aria-hidden="true" /> },
    { to: '/attendance', label: 'Attendance', icon: <CheckSquare size={20} aria-hidden="true" /> },
    { to: '/results', label: 'Results', icon: <BarChart3 size={20} aria-hidden="true" /> },
    { to: '/fees', label: 'Fees/Pay', icon: <CreditCard size={20} aria-hidden="true" /> },
    { to: '/notices', label: 'Notices', icon: <Megaphone size={20} aria-hidden="true" /> },
  ],
  Student: [
    { to: '/today', label: 'Today', icon: <ClipboardList size={20} aria-hidden="true" /> },
    { to: '/timetable', label: 'Timetable', icon: <Calendar size={20} aria-hidden="true" /> },
    { to: '/results', label: 'Results', icon: <BarChart3 size={20} aria-hidden="true" /> },
    { to: '/homework', label: 'Homework', icon: <BookOpen size={20} aria-hidden="true" /> },
    { to: '/materials', label: 'Materials', icon: <FileSpreadsheet size={20} aria-hidden="true" /> },
  ],
  Accountant: [
    { to: '/collect', label: 'Collect', icon: <Wallet size={20} aria-hidden="true" /> },
    { to: '/finance', label: 'Overview', icon: <Landmark size={20} aria-hidden="true" /> },
    { to: '/invoices', label: 'Invoices', icon: <Receipt size={20} aria-hidden="true" /> },
    { to: '/reports', label: 'Reports', icon: <BarChart3 size={20} aria-hidden="true" /> },
    { to: '/reconciliation', label: 'Reconcile', icon: <Scale size={20} aria-hidden="true" /> },
    { to: '/refunds', label: 'Refunds', icon: <ArrowLeftToLine size={20} aria-hidden="true" /> },
    { to: '/more', label: 'More', icon: <MoreHorizontal size={20} aria-hidden="true" /> },
  ],
  SchoolAdmin: [
    { to: '/dashboard', label: 'Dashboard', icon: <BarChart3 size={20} aria-hidden="true" /> },
    // OLD CODE — { to: '/people', label: 'People', icon: <Users size={20} aria-hidden="true" /> },
    { to: '/people', label: 'Student', icon: <Users size={20} aria-hidden="true" /> },
    { to: '/guardians', label: 'Guardians', icon: <Contact size={20} aria-hidden="true" /> },
    { to: '/admissions', label: 'Admissions', icon: <UserPlus size={20} aria-hidden="true" /> },
    { to: '/academics/classes', label: 'Academics', icon: <GraduationCap size={20} aria-hidden="true" />, children: [
      { to: '/academics/classes', label: 'Classes & Sections', icon: <GraduationCap size={20} aria-hidden="true" /> },
      { to: '/academics/subjects', label: 'Subjects', icon: <BookMarked size={20} aria-hidden="true" /> },
    ] },
    { to: '/calendar', label: 'Calendar', icon: <CalendarDays size={20} aria-hidden="true" /> },
    { to: '/exam-terms', label: 'Exams', icon: <CalendarRange size={20} aria-hidden="true" /> },
    { to: '/report-cards', label: 'Report Cards', icon: <Award size={20} aria-hidden="true" /> },
    { to: '/fees', label: 'Fees', icon: <DollarSign size={20} aria-hidden="true" /> },
    { to: '/reports', label: 'Reports', icon: <PieChart size={20} aria-hidden="true" /> },
    { to: '/comms', label: 'Comms', icon: <Megaphone size={20} aria-hidden="true" /> },
    { to: '/settings', label: 'Settings', icon: <Settings size={20} aria-hidden="true" /> },
  ],
  ExamCoordinator: [
    { to: '/marks', label: 'Marks', icon: <FilePen size={20} aria-hidden="true" /> },
    { to: '/exam-terms', label: 'Exams', icon: <CalendarRange size={20} aria-hidden="true" /> },
    { to: '/report-cards', label: 'Report Cards', icon: <Award size={20} aria-hidden="true" /> },
    { to: '/academics/subjects', label: 'Subjects', icon: <BookMarked size={20} aria-hidden="true" /> },
  ],
};

const FALLBACK_NAV: readonly NavItem[] = [
  { to: '/workspace', label: 'Home', icon: <Home size={20} aria-hidden="true" /> },
];

export function navForRole(role: Role): readonly NavItem[] {
  return NAV[role] ?? FALLBACK_NAV;
}
